import React, { useState } from 'react';
import { astroIntelligenceClient } from '../services/astroIntelligenceClient';

const AstroIntelligencePage = () => {
  const [birthData, setBirthData] = useState({
    name: '',
    day: 1,
    month: 1,
    year: 1990,
    hour: 12,
    minute: 0,
    latitude: 28.7041,
    longitude: 77.1025,
    timezone: 5.5,
    place_of_birth: ''
  });

  const [symbols, setSymbols] = useState('');
  const [portfolio, setPortfolio] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState('signals');

  const handleBirthDataChange = (field, value) => {
    setBirthData(prev => ({
      ...prev,
      [field]: field === 'name' || field === 'place_of_birth' ? value : parseFloat(value) || 0
    }));
  };

  const handleGetTradingSignals = async () => {
    if (!symbols.trim()) {
      alert('Please enter symbols');
      return;
    }

    setLoading(true);
    try {
      const symbolList = symbols.split(',').map(s => s.trim()).filter(s => s);
      const response = await astroIntelligenceClient.getTradingSignals(symbolList, birthData);
      setResults({ type: 'signals', data: response.data });
    } catch (error) {
      console.error('Error getting trading signals:', error);
      alert('Error getting trading signals: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGetPortfolioAnalysis = async () => {
    if (!portfolio.trim()) {
      alert('Please enter portfolio data');
      return;
    }

    setLoading(true);
    try {
      // Parse portfolio data (format: SYMBOL:QUANTITY,SYMBOL:QUANTITY)
      const portfolioData = {};
      portfolio.split(',').forEach(item => {
        const [symbol, quantity] = item.split(':').map(s => s.trim());
        if (symbol && quantity) {
          portfolioData[symbol] = parseFloat(quantity);
        }
      });

      const response = await astroIntelligenceClient.getPortfolioAnalysis(portfolioData, birthData);
      setResults({ type: 'portfolio', data: response.data });
    } catch (error) {
      console.error('Error getting portfolio analysis:', error);
      alert('Error getting portfolio analysis: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGetMarketTiming = async () => {
    setLoading(true);
    try {
      const response = await astroIntelligenceClient.getMarketTiming(birthData);
      setResults({ type: 'timing', data: response.data });
    } catch (error) {
      console.error('Error getting market timing:', error);
      alert('Error getting market timing: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getSignalColor = (signalType) => {
    switch (signalType) {
      case 'BUY': return '#10b981';
      case 'SELL': return '#ef4444';
      case 'HOLD': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getSignalIcon = (signalType) => {
    switch (signalType) {
      case 'BUY': return '↗️';
      case 'SELL': return '↘️';
      case 'HOLD': return '→';
      default: return '→';
    }
  };

  const formatConfidence = (confidence) => {
    return `${Math.round(confidence * 100)}%`;
  };

  return (
    <div style={styles.container}>
      {/* Hero Section */}
      <div style={styles.heroSection}>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>🌟 Astrological Intelligence for Trading</h1>
          <p style={styles.heroSubtitle}>
            Unlock deeper market insights through comprehensive astrological analysis and cosmic timing
          </p>
          <div style={styles.heroFeatures}>
            <div style={styles.featureItem}>
              <span style={styles.featureIcon}>🌙</span>
              <span>Planetary Analysis</span>
            </div>
            <div style={styles.featureItem}>
              <span style={styles.featureIcon}>🔮</span>
              <span>Dasha Periods</span>
            </div>
            <div style={styles.featureItem}>
              <span style={styles.featureIcon}>⭐</span>
              <span>Cosmic Timing</span>
            </div>
          </div>
        </div>
        
        {/* What It Does Section */}
        <div style={styles.explanationCard}>
          <h3 style={styles.cardTitle}>How Astrological Intelligence Works</h3>
          <div style={styles.stepsList}>
            <div style={styles.step}>
              <span style={styles.stepNumber}>1</span>
              <div>
                <strong>Birth Chart Generation:</strong> Create detailed astrological profiles using precise birth data (date, time, location)
              </div>
            </div>
            <div style={styles.step}>
              <span style={styles.stepNumber}>2</span>
              <div>
                <strong>Planetary Analysis:</strong> Analyze positions of Sun, Moon, Mars, Mercury, Jupiter, Venus, Saturn, Rahu, and Ketu
              </div>
            </div>
            <div style={styles.step}>
              <span style={styles.stepNumber}>3</span>
              <div>
                <strong>Dasha Periods:</strong> Understand current and upcoming planetary periods affecting your trading decisions
              </div>
            </div>
            <div style={styles.step}>
              <span style={styles.stepNumber}>4</span>
              <div>
                <strong>Market Timing:</strong> Get insights on favorable and challenging periods for different types of investments
              </div>
            </div>
          </div>
          
          <div style={styles.advantagesSection}>
            <h4 style={styles.advantagesTitle}>Key Advantages</h4>
            <div style={styles.advantagesList}>
              <div style={styles.advantage}>🎯 Personalized timing based on your unique birth chart</div>
              <div style={styles.advantage}>📅 Long-term perspective on favorable trading periods</div>
              <div style={styles.advantage}>🌟 Holistic approach combining multiple astrological factors</div>
              <div style={styles.advantage}>💾 Persistent profiles - generate once, use repeatedly</div>
            </div>
          </div>
          
          <div style={styles.warningBox}>
            <strong>Important:</strong> Astrological analysis requires accurate birth data including precise time and location. Results are for educational and research purposes only.
          </div>
        </div>
      </div>

      <div style={styles.content}>
        {/* Birth Data Form */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Birth Information</h2>
          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Name</label>
              <input
                type="text"
                value={birthData.name}
                onChange={(e) => handleBirthDataChange('name', e.target.value)}
                style={styles.input}
                placeholder="Enter your name"
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Birth Date</label>
              <div style={styles.dateInputs}>
                <input
                  type="number"
                  value={birthData.day}
                  onChange={(e) => handleBirthDataChange('day', e.target.value)}
                  style={styles.dateInput}
                  placeholder="Day"
                  min="1"
                  max="31"
                />
                <input
                  type="number"
                  value={birthData.month}
                  onChange={(e) => handleBirthDataChange('month', e.target.value)}
                  style={styles.dateInput}
                  placeholder="Month"
                  min="1"
                  max="12"
                />
                <input
                  type="number"
                  value={birthData.year}
                  onChange={(e) => handleBirthDataChange('year', e.target.value)}
                  style={styles.dateInput}
                  placeholder="Year"
                  min="1900"
                  max="2024"
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Birth Time</label>
              <div style={styles.timeInputs}>
                <input
                  type="number"
                  value={birthData.hour}
                  onChange={(e) => handleBirthDataChange('hour', e.target.value)}
                  style={styles.timeInput}
                  placeholder="Hour"
                  min="0"
                  max="23"
                />
                <input
                  type="number"
                  value={birthData.minute}
                  onChange={(e) => handleBirthDataChange('minute', e.target.value)}
                  style={styles.timeInput}
                  placeholder="Minute"
                  min="0"
                  max="59"
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Location</label>
              <div style={styles.locationInputs}>
                <input
                  type="number"
                  step="0.000001"
                  value={birthData.latitude}
                  onChange={(e) => handleBirthDataChange('latitude', e.target.value)}
                  style={styles.locationInput}
                  placeholder="Latitude"
                />
                <input
                  type="number"
                  step="0.000001"
                  value={birthData.longitude}
                  onChange={(e) => handleBirthDataChange('longitude', e.target.value)}
                  style={styles.locationInput}
                  placeholder="Longitude"
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Place of Birth</label>
              <input
                type="text"
                value={birthData.place_of_birth}
                onChange={(e) => handleBirthDataChange('place_of_birth', e.target.value)}
                style={styles.input}
                placeholder="Enter place of birth"
              />
            </div>
          </div>
        </div>

        {/* Analysis Tabs */}
        <div style={styles.section}>
          <div style={styles.tabContainer}>
            <button
              style={{
                ...styles.tab,
                ...(activeTab === 'signals' ? styles.activeTab : {})
              }}
              onClick={() => setActiveTab('signals')}
            >
              Trading Signals
            </button>
            <button
              style={{
                ...styles.tab,
                ...(activeTab === 'portfolio' ? styles.activeTab : {})
              }}
              onClick={() => setActiveTab('portfolio')}
            >
              Portfolio Analysis
            </button>
            <button
              style={{
                ...styles.tab,
                ...(activeTab === 'timing' ? styles.activeTab : {})
              }}
              onClick={() => setActiveTab('timing')}
            >
              Market Timing
            </button>
          </div>

          {/* Trading Signals Tab */}
          {activeTab === 'signals' && (
            <div style={styles.tabContent}>
              <h3 style={styles.tabTitle}>Get Trading Signals</h3>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Symbols (comma-separated)</label>
                <input
                  type="text"
                  value={symbols}
                  onChange={(e) => setSymbols(e.target.value)}
                  style={styles.input}
                  placeholder="e.g., INFY, TCS, RELIANCE"
                />
              </div>
              <button
                onClick={handleGetTradingSignals}
                disabled={loading}
                style={styles.button}
              >
                {loading ? 'Analyzing...' : 'Get Astro Signals'}
              </button>
            </div>
          )}

          {/* Portfolio Analysis Tab */}
          {activeTab === 'portfolio' && (
            <div style={styles.tabContent}>
              <h3 style={styles.tabTitle}>Portfolio Analysis</h3>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Portfolio (SYMBOL:QUANTITY format)</label>
                <input
                  type="text"
                  value={portfolio}
                  onChange={(e) => setPortfolio(e.target.value)}
                  style={styles.input}
                  placeholder="e.g., INFY:100, TCS:50, RELIANCE:25"
                />
              </div>
              <button
                onClick={handleGetPortfolioAnalysis}
                disabled={loading}
                style={styles.button}
              >
                {loading ? 'Analyzing...' : 'Analyze Portfolio'}
              </button>
            </div>
          )}

          {/* Market Timing Tab */}
          {activeTab === 'timing' && (
            <div style={styles.tabContent}>
              <h3 style={styles.tabTitle}>Market Timing Analysis</h3>
              <p style={styles.description}>
                Get optimal trading times based on astrological analysis
              </p>
              <button
                onClick={handleGetMarketTiming}
                disabled={loading}
                style={styles.button}
              >
                {loading ? 'Analyzing...' : 'Get Market Timing'}
              </button>
            </div>
          )}
        </div>

        {/* Results */}
        {results && (
          <div style={styles.resultsSection}>
            <h2 style={styles.resultsTitle}>Analysis Results</h2>
            
            {results.type === 'signals' && (
              <div style={styles.signalsContainer}>
                {results.data.signals.map((signal, index) => (
                  <div key={index} style={styles.signalCard}>
                    <div style={styles.signalHeader}>
                      <span style={styles.symbol}>{signal.symbol}</span>
                      <span style={{
                        ...styles.signalBadge,
                        backgroundColor: getSignalColor(signal.signal_type)
                      }}>
                        {getSignalIcon(signal.signal_type)} {signal.signal_type}
                      </span>
                      <span style={styles.confidence}>
                        {formatConfidence(signal.confidence)} confidence
                      </span>
                    </div>
                    <p style={styles.signalReason}>{signal.astro_reason}</p>
                    {signal.planetary_influence && signal.planetary_influence.length > 0 && (
                      <div style={styles.planetaryInfluence}>
                        <strong>Planetary Influences:</strong>
                        <ul style={styles.influenceList}>
                          {signal.planetary_influence.map((influence, i) => (
                            <li key={i} style={styles.influenceItem}>{influence}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {results.type === 'portfolio' && (
              <div style={styles.portfolioContainer}>
                <div style={styles.portfolioSummary}>
                  <h3 style={styles.summaryTitle}>Portfolio Summary</h3>
                  <div style={styles.summaryStats}>
                    <div style={styles.statItem}>
                      <span style={styles.statLabel}>Action:</span>
                      <span style={{
                        ...styles.statValue,
                        color: getSignalColor(results.data.portfolio_action.replace('_', ' '))
                      }}>
                        {results.data.portfolio_action.replace('_', ' ')}
                      </span>
                    </div>
                    <div style={styles.statItem}>
                      <span style={styles.statLabel}>Total Symbols:</span>
                      <span style={styles.statValue}>{results.data.total_symbols}</span>
                    </div>
                    <div style={styles.statItem}>
                      <span style={styles.statLabel}>Buy Signals:</span>
                      <span style={styles.statValue}>{results.data.buy_signals}</span>
                    </div>
                    <div style={styles.statItem}>
                      <span style={styles.statLabel}>Sell Signals:</span>
                      <span style={styles.statValue}>{results.data.sell_signals}</span>
                    </div>
                    <div style={styles.statItem}>
                      <span style={styles.statLabel}>Average Confidence:</span>
                      <span style={styles.statValue}>{formatConfidence(results.data.average_confidence)}</span>
                    </div>
                  </div>
                  <p style={styles.recommendation}>{results.data.recommendation}</p>
                </div>
              </div>
            )}

            {results.type === 'timing' && (
              <div style={styles.timingContainer}>
                <div style={styles.timingCard}>
                  <h3 style={styles.timingTitle}>Market Timing Analysis</h3>
                  <div style={styles.timingStats}>
                    <div style={styles.timingItem}>
                      <span style={styles.timingLabel}>Timing Score:</span>
                      <span style={styles.timingValue}>{Math.round(results.data.timing_score * 100)}%</span>
                    </div>
                    <div style={styles.timingItem}>
                      <span style={styles.timingLabel}>Recommendation:</span>
                      <span style={styles.timingValue}>{results.data.recommendation}</span>
                    </div>
                    <div style={styles.timingItem}>
                      <span style={styles.timingLabel}>Action:</span>
                      <span style={{
                        ...styles.timingValue,
                        color: getSignalColor(results.data.action)
                      }}>
                        {results.data.action}
                      </span>
                    </div>
                    <div style={styles.timingItem}>
                      <span style={styles.timingLabel}>Next Optimal Time:</span>
                      <span style={styles.timingValue}>
                        {new Date(results.data.next_optimal_time).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    backgroundColor: '#f8fafc',
    minHeight: '100vh'
  },
  heroSection: {
    background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
    borderRadius: '16px',
    padding: '40px',
    marginBottom: '40px',
    color: 'white'
  },
  heroContent: {
    textAlign: 'center',
    marginBottom: '30px'
  },
  heroTitle: {
    fontSize: '2.5rem',
    fontWeight: '700',
    margin: '0 0 16px 0',
    lineHeight: '1.2'
  },
  heroSubtitle: {
    fontSize: '1.2rem',
    opacity: '0.9',
    maxWidth: '600px',
    margin: '0 auto 30px auto',
    lineHeight: '1.5'
  },
  heroFeatures: {
    display: 'flex',
    justifyContent: 'center',
    gap: '30px',
    flexWrap: 'wrap',
    marginBottom: '30px'
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '1rem',
    fontWeight: '500'
  },
  featureIcon: {
    fontSize: '1.2rem'
  },
  explanationCard: {
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    padding: '30px',
    backdropFilter: 'blur(10px)'
  },
  cardTitle: {
    fontSize: '1.5rem',
    fontWeight: '600',
    margin: '0 0 20px 0',
    textAlign: 'center'
  },
  stepsList: {
    marginBottom: '30px'
  },
  step: {
    display: 'flex',
    gap: '15px',
    alignItems: 'flex-start',
    marginBottom: '20px',
    padding: '15px',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '8px'
  },
  stepNumber: {
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '0.9rem',
    flexShrink: 0
  },
  advantagesSection: {
    marginBottom: '20px'
  },
  advantagesTitle: {
    fontSize: '1.2rem',
    fontWeight: '600',
    margin: '0 0 15px 0'
  },
  advantagesList: {
    display: 'grid',
    gap: '8px'
  },
  advantage: {
    fontSize: '0.95rem',
    opacity: '0.9'
  },
  warningBox: {
    background: 'rgba(255, 255, 255, 0.1)',
    padding: '15px',
    borderRadius: '8px',
    fontSize: '0.9rem',
    borderLeft: '4px solid rgba(255, 255, 255, 0.3)'
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px',
    padding: '20px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  title: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: '#1f2937',
    margin: '0 0 10px 0'
  },
  subtitle: {
    fontSize: '1.1rem',
    color: '#6b7280',
    margin: '0'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  section: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  sectionTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#1f2937',
    margin: '0 0 20px 0'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#374151'
  },
  input: {
    padding: '12px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'border-color 0.2s'
  },
  dateInputs: {
    display: 'flex',
    gap: '10px'
  },
  dateInput: {
    flex: 1,
    padding: '12px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '1rem'
  },
  timeInputs: {
    display: 'flex',
    gap: '10px'
  },
  timeInput: {
    flex: 1,
    padding: '12px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '1rem'
  },
  locationInputs: {
    display: 'flex',
    gap: '10px'
  },
  locationInput: {
    flex: 1,
    padding: '12px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '1rem'
  },
  tabContainer: {
    display: 'flex',
    borderBottom: '2px solid #e5e7eb',
    marginBottom: '20px'
  },
  tab: {
    padding: '12px 24px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#6b7280',
    borderBottom: '3px solid transparent',
    transition: 'all 0.2s'
  },
  activeTab: {
    color: '#3b82f6',
    borderBottomColor: '#3b82f6'
  },
  tabContent: {
    padding: '20px 0'
  },
  tabTitle: {
    fontSize: '1.3rem',
    fontWeight: 'bold',
    color: '#1f2937',
    margin: '0 0 20px 0'
  },
  description: {
    color: '#6b7280',
    marginBottom: '20px'
  },
  inputGroup: {
    marginBottom: '20px'
  },
  button: {
    padding: '12px 24px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  resultsSection: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  resultsTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#1f2937',
    margin: '0 0 20px 0'
  },
  signalsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  signalCard: {
    padding: '20px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    backgroundColor: '#f9fafb'
  },
  signalHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '12px',
    flexWrap: 'wrap'
  },
  symbol: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: '#1f2937'
  },
  signalBadge: {
    padding: '6px 12px',
    borderRadius: '20px',
    color: 'white',
    fontSize: '0.9rem',
    fontWeight: '600'
  },
  confidence: {
    fontSize: '0.9rem',
    color: '#6b7280',
    fontWeight: '500'
  },
  signalReason: {
    color: '#374151',
    margin: '0 0 12px 0',
    lineHeight: '1.5'
  },
  planetaryInfluence: {
    marginTop: '12px'
  },
  influenceList: {
    margin: '8px 0 0 0',
    paddingLeft: '20px'
  },
  influenceItem: {
    color: '#6b7280',
    marginBottom: '4px'
  },
  portfolioContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  portfolioSummary: {
    padding: '20px',
    backgroundColor: '#f9fafb',
    borderRadius: '12px',
    border: '2px solid #e5e7eb'
  },
  summaryTitle: {
    fontSize: '1.3rem',
    fontWeight: 'bold',
    color: '#1f2937',
    margin: '0 0 16px 0'
  },
  summaryStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '16px'
  },
  statItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  statLabel: {
    fontSize: '0.9rem',
    color: '#6b7280',
    fontWeight: '500'
  },
  statValue: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#1f2937'
  },
  recommendation: {
    fontSize: '1rem',
    color: '#374151',
    fontStyle: 'italic',
    margin: '0',
    padding: '12px',
    backgroundColor: '#f3f4f6',
    borderRadius: '8px'
  },
  timingContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  timingCard: {
    padding: '20px',
    backgroundColor: '#f9fafb',
    borderRadius: '12px',
    border: '2px solid #e5e7eb'
  },
  timingTitle: {
    fontSize: '1.3rem',
    fontWeight: 'bold',
    color: '#1f2937',
    margin: '0 0 16px 0'
  },
  timingStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px'
  },
  timingItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  timingLabel: {
    fontSize: '0.9rem',
    color: '#6b7280',
    fontWeight: '500'
  },
  timingValue: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#1f2937'
  }
};

export default AstroIntelligencePage;
